// src/pages/Register.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

// Role suffixes appended to the monthly base code.
// e.g. base code "BL03" -> learner: BL03, tutor: BL03T, mentor: BL03M, official: BL03O
const ROLE_SUFFIXES = {
  T: 'tutor',
  M: 'mentor',
  O: 'official',
};

// Roles that need to pick a cohort at registration
const ROLES_REQUIRING_COHORT = ['learner', 'official'];

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    accessCode: '',
    cohortId: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resolvedRole, setResolvedRole] = useState(null);
  const [baseCode, setBaseCode] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [cohortsLoading, setCohortsLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch the cohort list once on mount, used for the learner/official dropdown
  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'cohorts'));
        const list = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCohorts(list);
      } catch (err) {
        console.error('Failed to load cohorts:', err);
      } finally {
        setCohortsLoading(false);
      }
    };
    fetchCohorts();
  }, []);

  // Fetch the current base registration code once, so we can resolve the
  // role locally (for showing/hiding the cohort field) as the user types.
  useEffect(() => {
    const fetchBaseCode = async () => {
      try {
        const configDoc = await getDoc(doc(db, 'config', 'registration'));
        if (configDoc.exists()) {
          setBaseCode(configDoc.data().currentCode || '');
        }
      } catch (err) {
        console.error('Failed to load registration config:', err);
      }
    };
    fetchBaseCode();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Parses an access code against the current base code, returning
  // { role } or throwing a user-facing error.
  const resolveAccessCode = (rawCode, currentBaseCode) => {
    const code = rawCode.trim().toUpperCase();
    const base = currentBaseCode.toUpperCase();

    if (code === base) {
      return { role: 'learner' };
    }

    if (code.startsWith(base) && code.length === base.length + 1) {
      const suffix = code.slice(base.length);
      const role = ROLE_SUFFIXES[suffix];
      if (role) {
        return { role };
      }
    }

    throw new Error('Invalid access code. Please check with your coordinator.');
  };

  // Live-resolve the role as the access code field changes, purely to
  // show/hide the cohort dropdown. Validation against expiry still
  // happens server-side (well, Firestore-side) on submit.
  useEffect(() => {
    if (!baseCode || !formData.accessCode.trim()) {
      setResolvedRole(null);
      return;
    }
    try {
      const { role } = resolveAccessCode(formData.accessCode, baseCode);
      setResolvedRole(role);
    } catch {
      setResolvedRole(null);
    }
  }, [formData.accessCode, baseCode]);

  const validateAccessCode = async (code) => {
    const configDoc = await getDoc(doc(db, 'config', 'registration'));

    if (!configDoc.exists()) {
      throw new Error('Registration is not currently available. Please contact your admin.');
    }

    const config = configDoc.data();

    const expiresAt = config.expiresAt?.toDate ? config.expiresAt.toDate() : new Date(config.expiresAt);
    if (new Date() > expiresAt) {
      throw new Error('This access code has expired. Please contact your coordinator for the current code.');
    }

    const { role } = resolveAccessCode(code, config.currentCode);

    return { validCode: code.trim().toUpperCase(), role };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (!formData.accessCode.trim()) {
      setError('Please enter your access code.');
      return;
    }

    setLoading(true);

    try {
      const { validCode, role } = await validateAccessCode(formData.accessCode.trim());

      if (ROLES_REQUIRING_COHORT.includes(role) && !formData.cohortId) {
        setError('Please select your cohort.');
        setLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const userDoc = {
        id: userCredential.user.uid,
        name: formData.name,
        email: formData.email,
        role,
        status: 'active',
        registrationCode: validCode,
        createdAt: serverTimestamp(),
      };

      if (ROLES_REQUIRING_COHORT.includes(role)) {
        userDoc.cohortId = formData.cohortId;
      }

      await setDoc(doc(db, 'users', userCredential.user.uid), userDoc);

      navigate('/');
    } catch (err) {
      console.error('Registration error:', err);

      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const showCohortField = resolvedRole && ROLES_REQUIRING_COHORT.includes(resolvedRole);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      backgroundColor: 'var(--color-gray-50, #f9fafb)'
    }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>

        {/* Logo / Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img
            src="/logo.png"
            alt="Litvill Lessons"
            style={{ width: '180px', marginBottom: '1rem' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 style={{
            color: 'var(--color-teal, #0d9488)',
            marginBottom: '0.5rem',
            fontSize: '1.75rem',
            fontWeight: 700
          }}>
            Join Litvill Lessons
          </h1>
          <p style={{ color: 'var(--color-gray-600, #4b5563)', margin: 0 }}>
            Create your account
          </p>
        </div>

        {/* Form Card */}
        <div className="card" style={{ padding: '2rem' }}>
          {error && (
            <div style={{
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
              backgroundColor: '#fef2f2',
              color: '#b91c1c',
              borderRadius: 'var(--radius-md, 8px)',
              fontSize: '0.875rem',
              border: '1px solid #fecaca'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <Field
              label="Full Name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              disabled={loading}
              required
            />

            <Field
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              disabled={loading}
              required
            />

            <Field
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="At least 6 characters"
              disabled={loading}
              required
            />

            <Field
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              disabled={loading}
              required
            />

            <Field
              label="Access Code"
              name="accessCode"
              type="text"
              value={formData.accessCode}
              onChange={handleChange}
              placeholder="e.g. BL03"
              disabled={loading}
              required
            />
            <p style={{
              fontSize: '0.75rem',
              color: 'var(--color-gray-500, #6b7280)',
              marginTop: '-0.75rem',
              marginBottom: '1.25rem'
            }}>
              Get your access code from your coordinator. Codes change monthly.
            </p>

            {showCohortField && (
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="cohortId"
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.375rem',
                    color: 'var(--color-gray-700, #374151)'
                  }}
                >
                  Cohort
                </label>
                <select
                  id="cohortId"
                  name="cohortId"
                  value={formData.cohortId}
                  onChange={handleChange}
                  disabled={loading || cohortsLoading}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    border: '1px solid var(--color-gray-300, #d1d5db)',
                    borderRadius: 'var(--radius-md, 8px)',
                    backgroundColor: loading || cohortsLoading ? 'var(--color-gray-100, #f3f4f6)' : 'white',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                >
                  <option value="" disabled>
                    {cohortsLoading ? 'Loading cohorts…' : 'Select your cohort'}
                  </option>
                  {cohorts.map(cohort => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name}
                    </option>
                  ))}
                </select>
                <p style={{
                  fontSize: '0.75rem',
                  color: 'var(--color-gray-500, #6b7280)',
                  marginTop: '0.375rem',
                }}>
                  Not sure which to pick? Choose OTHER — your admin can update this later.
                </p>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ width: '100%', marginTop: '0.25rem' }}
            >
              {loading ? 'Creating Account…' : 'Create Account'}
            </button>
          </form>

          <div style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'var(--color-gray-600, #4b5563)'
          }}>
            Already have an account?{' '}
            <Link
              to="/login"
              style={{
                color: 'var(--color-teal, #0d9488)',
                fontWeight: 500,
                textDecoration: 'none'
              }}
            >
              Sign in
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

// Small reusable field component — avoids dependency on FormInputs from other app
function Field({ label, name, type, value, onChange, placeholder, disabled, required }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label
        htmlFor={name}
        style={{
          display: 'block',
          fontSize: '0.875rem',
          fontWeight: 500,
          marginBottom: '0.375rem',
          color: 'var(--color-gray-700, #374151)'
        }}
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          border: '1px solid var(--color-gray-300, #d1d5db)',
          borderRadius: 'var(--radius-md, 8px)',
          backgroundColor: disabled ? 'var(--color-gray-100, #f3f4f6)' : 'white',
          boxSizing: 'border-box',
          outline: 'none',
        }}
      />
    </div>
  );
}